'use client';

import { Box, Stack, Theme } from '@mui/material';
import { ReactNode } from 'react';

export type PageShellProps = {
  /**
   * The logo component to be displayed in the header section
   */
  logo: ReactNode;
  /**
   * The title displayed in the header section next to the logo.
   * Can be a string or a custom ReactNode component.
   */
  title: ReactNode;
  /**
   * The content to be rendered below the header section
   */
  children: ReactNode;
  /**
   * Optional slot for custom options/actions displayed on the right side of the header
   */
  headerOptions?: ReactNode;
};

/**
 * PageShell is a layout component that consists of two parts:
 * - Header: Logo on the left, title on the right, optional actions on the far right
 * - Content: Any content passed as children
 *
 * @example
 * ```tsx
 * <PageShell
 *   logo={<Logo size="medium" />}
 *   title="Verify a New Entity"
 * >
 *   <EntityTypeSelector />
 * </PageShell>
 * ```
 *
 * @example
 * // With custom image logo
 * ```tsx
 * <PageShell
 *   logo={<img src="/my-logo.svg" alt="Logo" height={40} />}
 *   title="My Custom Page"
 * >
 *   <MyContent />
 * </PageShell>
 * ```
 *
 * @example
 * // With header options
 * ```tsx
 * <PageShell
 *   logo={<Logo size="medium" />}
 *   title="My Page"
 *   headerOptions={<Button>Settings</Button>}
 * >
 *   <MyContent />
 * </PageShell>
 * ```
 */
export default function PageShell({
  logo,
  title,
  children,
  headerOptions,
}: Readonly<PageShellProps>) {
  return (
    <Stack
      sx={{
        width: '100%',
        minHeight: '100%',
      }}
      data-component="page-shell"
    >
      {/* Header Section */}
      <Stack
        direction="row"
        alignItems="center"
        sx={(theme: Theme) => ({
          gap: theme.semantic.spacing.lg,
          paddingRight: theme.semantic.spacing.lg,
          borderBottom: `${theme.semantic.spacing['4xs']} solid ${theme.palette.divider}`,
        })}
        data-section="header"
      >
        {/* Logo */}
        <Box sx={{ flexShrink: 0, alignSelf: 'stretch', display: 'flex' }}>{logo}</Box>

        {/* Title */}
        <Box sx={{ flex: 1 }}>{title}</Box>

        {/* Header Options */}
        {headerOptions && (
          <Box sx={{ flexShrink: 0 }} data-slot="header-options">
            {headerOptions}
          </Box>
        )}
      </Stack>

      {/* Content Section */}
      <Box sx={{ flex: 1 }} data-section="content">
        {children}
      </Box>
    </Stack>
  );
}
